import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Configure axios to always send credentials and token
axios.defaults.withCredentials = true;

// Add request interceptor to include token
axios.interceptors.request.use((config) => {
  const token = localStorage.getItem('cleanpro_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Normalize Pydantic validation errors into readable messages
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.data?.detail) {
      const detail = error.response.data.detail;
      if (Array.isArray(detail)) {
        const msgs = detail.map(d => {
          const field = (d.loc || []).filter(l => l !== 'body').join(' > ') || 'field';
          const type = d.type || '';
          if (type === 'missing') return `${field} is required`;
          if (type === 'value_error') return d.msg || `${field} is invalid`;
          return d.msg || `${field}: validation error`;
        });
        error.response.data.detail = msgs.join('. ');
      }
    }
    return Promise.reject(error);
  }
);

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [permissions, setPermissions] = useState({ permissions: [], modules: {} });
  const [loading, setLoading] = useState(true);

  const refreshPermissions = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/auth/effective-permissions`);
      setPermissions(r.data || { permissions: [], modules: {} });
    } catch (e) {
      setPermissions({ permissions: [], modules: {} });
    }
  }, []);

  const checkAuth = useCallback(async () => {
    const token = localStorage.getItem('cleanpro_token');
    if (!token) {
      setUser(null);
      setPermissions({ permissions: [], modules: {} });
      setLoading(false);
      return;
    }
    
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
      await refreshPermissions();
    } catch (error) {
      // Token invalid, clear it
      localStorage.removeItem('cleanpro_token');
      setUser(null);
      setPermissions({ permissions: [], modules: {} });
    } finally {
      setLoading(false);
    }
  }, [refreshPermissions]);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, 
      { email, password }
    );
    // Store token for persistence
    if (response.data.token) {
      localStorage.setItem('cleanpro_token', response.data.token);
    }
    setUser(response.data);
    return response.data;
  };

  const register = async (email, password, name, role = 'staff') => {
    const response = await axios.post(`${API}/auth/register`,
      { email, password, name, role }
    );
    // Store token for persistence
    if (response.data.token) {
      localStorage.setItem('cleanpro_token', response.data.token);
    }
    setUser(response.data);
    return response.data;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {});
    } catch (error) {
      console.error('Logout error:', error);
    }
    localStorage.removeItem('cleanpro_token');
    setUser(null);
  };

  const logoutAll = async () => {
    await axios.post(`${API}/auth/logout-all`, {});
    localStorage.removeItem('cleanpro_token');
    setUser(null);
  };

  const loginWithGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const exchangeSession = async (sessionId) => {
    const response = await axios.post(`${API}/auth/session`,
      { session_id: sessionId }
    );
    // Store session token for persistence (Google OAuth flow)
    // Note: For OAuth, we use the session_token from the response
    if (response.data.token) {
      localStorage.setItem('cleanpro_token', response.data.token);
    }
    setUser(response.data);
    return response.data;
  };

  const isAdmin = user?.role === 'admin';
  const hasModule = useCallback((m) => !!permissions?.modules?.[m], [permissions]);
  const hasPerm = useCallback((p) => (permissions?.permissions || []).includes(p), [permissions]);

  // Keep permissions in sync after login too.
  useEffect(() => {
    if (user) { refreshPermissions(); }
  }, [user?.user_id, refreshPermissions]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <AuthContext.Provider value={{
      user,
      setUser,
      loading,
      login,
      register,
      logout,
      logoutAll,
      loginWithGoogle,
      exchangeSession,
      checkAuth,
      isAdmin,
      permissions,
      hasModule,
      hasPerm,
      refreshPermissions,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
